import React, { useState, useEffect } from 'react';
import { api } from '../api';

const CATEGORIES = ['Restaurant / Food','Retail / Shopping','Auto Dealer','Real Estate','Legal Services','Home Services','Health & Wellness','Entertainment','Technology','Finance / Banking','Other'];

export default function AdPlatform() {
  const [tab, setTab]           = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [step, setStep]         = useState(1);
  const [creating, setCreating] = useState(false);

  const [brief, setBrief] = useState({ business_name:'', business_category:'', offer:'', cta:'', website:'', voice_id:'voice_neutral' });
  const [creativeOptions, setCreativeOptions] = useState([]);
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [generatingCreative, setGeneratingCreative] = useState(false);

  const [campaign, setCampaign] = useState({
    name:'', targeting_geo:'national', targeting_format:'all', targeting_time:'all_day',
    daily_budget:50, total_budget:500, cpm_target:10,
    flight_start: new Date().toISOString().split('T')[0],
    flight_end: new Date(Date.now()+30*24*60*60*1000).toISOString().split('T')[0]
  });

  const setBrievField = (k,v) => setBrief(p => ({...p,[k]:v}));
  const setCamp = (k,v) => setCampaign(p => ({...p,[k]:v}));

  useEffect(() => {
    api.get('/broadcast/ads/campaigns').catch(() => ({ campaigns:[] }))
      .then(d => setCampaigns(d?.campaigns || []))
      .finally(() => setLoading(false));
  }, []);

  const generateCreative = async () => {
    setGeneratingCreative(true);
    try {
      const d = await api.post('/broadcast/ads/creative/generate', brief);
      setCreativeOptions(d.variations || []);
      setStep(2);
    } catch(e) { alert(e.message); }
    finally { setGeneratingCreative(false); }
  };

  // F3 three-path audio: a creative starts as text (path c); Generate audio (b) or Upload audio (a) attach audio.
  const [audioBusy, setAudioBusy] = useState(null);
  const patchCreative = (id, patch) => setCreativeOptions(opts => opts.map(o => o.id === id ? { ...o, ...patch } : o));
  const generateAudio = async (id) => {
    setAudioBusy(id);
    try {
      const d = await api.post(`/broadcast/ads/creative/${id}/generate-audio`, {});
      patchCreative(id, { audio_source: d.audio_source, audio_url: d.audio_url });
    } catch(e) { alert(e.message); }
    finally { setAudioBusy(null); }
  };
  const uploadAudio = async (id, file) => {
    if (!file) return;
    setAudioBusy(id);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(',')[1]);
        r.onerror = rej; r.readAsDataURL(file);
      });
      const d = await api.post(`/broadcast/ads/creative/${id}/upload-audio`, { file_base64: b64, content_type: file.type || 'audio/mpeg' });
      patchCreative(id, { audio_source: d.audio_source, audio_url: d.audio_url });
    } catch(e) { alert(e.message); }
    finally { setAudioBusy(null); }
  };

  const launchCampaign = async () => {
    setCreating(true);
    try {
      await api.post('/broadcast/ads/campaigns', { ...campaign, creative_id: selectedCreative?.id });
      const d = await api.get('/broadcast/ads/campaigns').catch(() => ({ campaigns:[] }));
      setCampaigns(d?.campaigns || []);
      setShowCreate(false); setStep(1);
      setBrief({ business_name:'', business_category:'', offer:'', cta:'', website:'', voice_id:'voice_neutral' });
      setCreativeOptions([]); setSelectedCreative(null);
    } catch(e) { alert(e.message); }
    finally { setCreating(false); }
  };

  const Tab = ({ id, label }) => (
    <button onClick={() => setTab(id)} style={{ padding:'8px 16px', fontSize:13, fontWeight:600, border:'none', cursor:'pointer', borderRadius:8, background: tab===id ? '#f59e0b' : 'transparent', color: tab===id ? '#fff' : '#64748b' }}>{label}</button>
  );

  const statusColor = { active:'#22c55e', paused:'#f59e0b', completed:'#94a3b8', draft:'#6366f1' };

  return (
    <div>
      <div style={{ background:'linear-gradient(135deg,#78350f,#b45309)', borderRadius:12, padding:'16px 20px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ color:'#fff', fontWeight:700, fontSize:15 }}>💰 Programmatic Ad Platform</div>
          <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12, marginTop:2 }}>Create audio ads with AI · Buy airtime across the Groovie network</div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ background:'#fff', color:'#b45309', border:'none', borderRadius:8, padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer' }}>
          + New Campaign
        </button>
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:20, background:'#f8fafc', padding:4, borderRadius:10, width:'fit-content' }}>
        <Tab id="campaigns" label="My Campaigns" />
        <Tab id="revenue"   label="Station Revenue" />
        <Tab id="network"   label="Network Stats" />
      </div>

      {tab === 'campaigns' && (
        <div>
          {campaigns.length === 0 ? (
            <div style={{ textAlign:'center', padding:'50px 20px', background:'#fff', borderRadius:12, border:'2px dashed #e2e8f0' }}>
              <div style={{ fontSize:48, marginBottom:12 }}>💰</div>
              <h3 style={{ margin:'0 0 8px', color:'#0f172a' }}>No campaigns yet</h3>
              <p style={{ color:'#64748b', fontSize:13, margin:'0 0 20px' }}>Create your first AI-generated audio ad campaign. No audio file required — we generate it for you.</p>
              <button onClick={() => setShowCreate(true)} style={{ padding:'10px 24px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#f59e0b,#d97706)', color:'#fff', fontWeight:700, cursor:'pointer' }}>
                Create First Campaign
              </button>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {campaigns.map((c, i) => (
                <div key={i} style={{ background:'#fff', borderRadius:12, padding:'16px 20px', border:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:16 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:'#0f172a', marginBottom:2 }}>{c.name}</div>
                    <div style={{ fontSize:12, color:'#64748b' }}>${c.daily_budget}/day · {c.targeting_geo} · CPM target ${c.cpm_target}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:16, fontWeight:800, color:'#0f172a' }}>{c.impressions_served?.toLocaleString() || 0}</div>
                    <div style={{ fontSize:11, color:'#94a3b8' }}>impressions</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:16, fontWeight:800, color:'#10b981' }}>${((c.spend_to_date_cents||0)/100).toFixed(2)}</div>
                    <div style={{ fontSize:11, color:'#94a3b8' }}>spent</div>
                  </div>
                  <span style={{ fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:20, background:`${(statusColor[c.status]||'#94a3b8')}18`, color: statusColor[c.status]||'#94a3b8' }}>{c.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'revenue' && (
        <div style={{ background:'#fff', borderRadius:12, padding:24, border:'1px solid #f1f5f9' }}>
          <h3 style={{ margin:'0 0 4px', fontSize:15, fontWeight:700 }}>Station Ad Revenue</h3>
          <p style={{ margin:'0 0 20px', fontSize:13, color:'#64748b' }}>Your 65% revenue share from ads served on your stations. Payouts via Stripe Connect on 30-day net.</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:20 }}>
            {[['This Month','$0.00'],['Last Month','$0.00'],['All Time','$0.00']].map(([l,v]) => (
              <div key={l} style={{ padding:16, borderRadius:10, background:'#f8fafc', border:'1px solid #f1f5f9', textAlign:'center' }}>
                <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:24, fontWeight:800, color:'#0f172a' }}>{v}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize:13, color:'#94a3b8' }}>Revenue grows as more advertisers join the network and your station's inventory fills. Join the ad network in Settings to start earning.</p>
        </div>
      )}

      {tab === 'network' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16 }}>
          {[['Network Stations','—'],['Active Advertisers','—'],['Avg Network CPM','$10.00'],['Total Impressions Today','—']].map(([l,v]) => (
            <div key={l} style={{ background:'#fff', borderRadius:12, padding:20, border:'1px solid #f1f5f9', textAlign:'center' }}>
              <div style={{ fontSize:28, fontWeight:800, color:'#0f172a', marginBottom:4 }}>{v}</div>
              <div style={{ fontSize:12, color:'#64748b' }}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreate && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <div>
                <h3 style={{ margin:0, fontSize:17, fontWeight:700 }}>New Ad Campaign</h3>
                <p style={{ margin:'3px 0 0', fontSize:12, color:'#64748b' }}>Step {step} of 3</p>
              </div>
              <button onClick={() => { setShowCreate(false); setStep(1); }} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#64748b' }}>×</button>
            </div>

            {/* Step indicator */}
            <div style={{ display:'flex', gap:6, marginBottom:22 }}>
              {['Ad Brief','Ad Creative','Campaign'].map((s,i) => (
                <div key={s} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                  <div style={{ height:3, width:'100%', borderRadius:2, background: step>i ? '#f59e0b' : '#e2e8f0' }} />
                  <span style={{ fontSize:10, color: step>i ? '#b45309' : '#94a3b8', fontWeight:600 }}>{s}</span>
                </div>
              ))}
            </div>

            {step === 1 && (
              <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
                <p style={{ margin:'0 0 8px', fontSize:13, color:'#64748b' }}>Tell us about your business and what you want to advertise. Our AI will write 3 professional 30-second ad scripts.</p>
                {['business_name','offer','website'].map(k => (
                  <div key={k}>
                    <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4, textTransform:'capitalize' }}>{k.replace('_',' ')}</label>
                    <input value={brief[k]} onChange={e => setBrievField(k, e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} onFocus={e => e.target.style.borderColor='#f59e0b'} onBlur={e => e.target.style.borderColor='#e2e8f0'} />
                  </div>
                ))}
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Business Category</label>
                  <select value={brief.business_category} onChange={e => setBrievField('business_category', e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none' }}>
                    <option value="">Select category…</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Call to Action</label>
                  <input value={brief.cta} onChange={e => setBrievField('cta', e.target.value)} placeholder="e.g. Call us today, Visit our website, Come in this weekend" style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} onFocus={e => e.target.style.borderColor='#f59e0b'} onBlur={e => e.target.style.borderColor='#e2e8f0'} />
                </div>
                <button onClick={generateCreative} disabled={generatingCreative || !brief.business_name} style={{ padding:11, borderRadius:8, border:'none', background: generatingCreative ? '#fde68a' : 'linear-gradient(135deg,#f59e0b,#d97706)', color:'#fff', fontWeight:700, cursor: generatingCreative?'not-allowed':'pointer' }}>
                  {generatingCreative ? '✨ Generating 3 Ad Scripts…' : '✨ Generate AI Ad Scripts'}
                </button>
              </div>
            )}

            {step === 2 && (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <p style={{ margin:0, fontSize:13, color:'#64748b' }}>Choose the ad script you prefer. We'll voice it professionally.</p>
                {creativeOptions.length === 0 ? (
                  <div style={{ textAlign:'center', padding:30 }}>
                    <div style={{ fontSize:32, marginBottom:8 }}>✍️</div>
                    <p style={{ color:'#64748b', fontSize:13 }}>Ad scripts will appear here after generation. For now, a sample is shown below.</p>
                    {[
                      { tone:'Direct', script:`${brief.business_name || 'Our business'} is here for you. ${brief.offer || 'Great deals available'}. ${brief.cta || 'Visit us today'}.` },
                      { tone:'Warm', script:`When you're looking for quality, think ${brief.business_name || 'us'}. ${brief.offer || 'We have what you need'}. Give us a call — we'd love to help.` },
                      { tone:'Energetic', script:`Hey! ${brief.business_name || 'We'} want YOUR business! ${brief.offer || 'Amazing deals'} — don't miss out! ${brief.cta || 'Come see us!'}` },
                    ].map((c, i) => (
                      <div key={i} onClick={() => setSelectedCreative({ id:`sample_${i}`, ...c })} style={{ padding:14, borderRadius:10, border:`2px solid ${selectedCreative?.tone===c.tone ? '#f59e0b' : '#e2e8f0'}`, background: selectedCreative?.tone===c.tone ? '#fffbeb' : '#fff', cursor:'pointer', marginBottom:10 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#b45309', textTransform:'uppercase', marginBottom:4 }}>{c.tone} tone</div>
                        <div style={{ fontSize:13, color:'#374151' }}>{c.script}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  creativeOptions.map((c, i) => (
                    <div key={i} onClick={() => setSelectedCreative(c)} style={{ padding:14, borderRadius:10, border:`2px solid ${selectedCreative?.id===c.id ? '#f59e0b' : '#e2e8f0'}`, background: selectedCreative?.id===c.id ? '#fffbeb' : '#fff', cursor:'pointer', marginBottom:10 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'#b45309', textTransform:'uppercase', marginBottom:4 }}>{c.tone} tone</div>
                      <div style={{ fontSize:13, color:'#374151' }}>{c.script}</div>
                      {/* F3 three-way audio source: Text (default) / Generate audio / Upload audio */}
                      <div onClick={e => e.stopPropagation()} style={{ marginTop:10, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:6, textTransform:'uppercase',
                          background: c.audio_source==='generated' ? '#dbeafe' : c.audio_source==='upload' ? '#dcfce7' : '#f1f5f9',
                          color: c.audio_source==='generated' ? '#1d4ed8' : c.audio_source==='upload' ? '#15803d' : '#64748b' }}>
                          {c.audio_source==='generated' ? 'Generated audio' : c.audio_source==='upload' ? 'Uploaded audio' : 'Text only'}
                        </span>
                        <button type="button" disabled={audioBusy===c.id} onClick={() => generateAudio(c.id)} style={{ fontSize:11, padding:'4px 10px', borderRadius:6, border:'1px solid #93c5fd', background:'#eff6ff', color:'#1d4ed8', cursor: audioBusy===c.id?'wait':'pointer', fontWeight:600 }}>
                          {audioBusy===c.id ? 'Working…' : 'Generate audio'}
                        </button>
                        <label style={{ fontSize:11, padding:'4px 10px', borderRadius:6, border:'1px solid #86efac', background:'#f0fdf4', color:'#15803d', cursor:'pointer', fontWeight:600 }}>
                          Upload audio
                          <input type="file" accept="audio/*" style={{ display:'none' }} onChange={e => uploadAudio(c.id, e.target.files[0])} />
                        </label>
                        {c.audio_url && <a href={c.audio_url} target="_blank" rel="noreferrer" style={{ fontSize:11, color:'#b45309', fontWeight:700 }}>▶ Play</a>}
                      </div>
                    </div>
                  ))
                )}
                <div style={{ display:'flex', gap:10 }}>
                  <button onClick={() => setStep(1)} style={{ flex:1, padding:10, borderRadius:8, border:'1.5px solid #e2e8f0', background:'#fff', fontWeight:600, cursor:'pointer', color:'#374151' }}>← Back</button>
                  <button onClick={() => setStep(3)} disabled={!selectedCreative} style={{ flex:2, padding:10, borderRadius:8, border:'none', background: selectedCreative ? 'linear-gradient(135deg,#f59e0b,#d97706)' : '#fde68a', color:'#fff', fontWeight:700, cursor: selectedCreative?'pointer':'not-allowed' }}>
                    Use This Script →
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Campaign Name</label>
                  <input value={campaign.name} onChange={e => setCamp('name', e.target.value)} placeholder={`${brief.business_name} — ${brief.offer}`} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Geography</label>
                    <select value={campaign.targeting_geo} onChange={e => setCamp('targeting_geo', e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13 }}>
                      <option value="national">National</option>
                      <option value="regional">Regional</option>
                      <option value="local">Local / Metro</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Time of Day</label>
                    <select value={campaign.targeting_time} onChange={e => setCamp('targeting_time', e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13 }}>
                      <option value="all_day">All Day</option>
                      <option value="morning_drive">Morning Drive (6–10am)</option>
                      <option value="afternoon_drive">Afternoon Drive (4–7pm)</option>
                      <option value="evening">Evening</option>
                      <option value="overnight">Overnight</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Daily Budget ($)</label>
                    <input type="number" min={10} value={campaign.daily_budget} onChange={e => setCamp('daily_budget', +e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Total Budget ($)</label>
                    <input type="number" min={50} value={campaign.total_budget} onChange={e => setCamp('total_budget', +e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Start Date</label>
                    <input type="date" value={campaign.flight_start} onChange={e => setCamp('flight_start', e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>End Date</label>
                    <input type="date" value={campaign.flight_end} onChange={e => setCamp('flight_end', e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
                  </div>
                </div>
                <div style={{ background:'#fffbeb', borderRadius:8, padding:'12px 14px', border:'1px solid #fde68a', fontSize:13, color:'#92400e' }}>
                  <strong>Estimated reach:</strong> {Math.round((campaign.total_budget / campaign.cpm_target) * 1000).toLocaleString()} impressions at ${campaign.cpm_target} CPM target
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <button onClick={() => setStep(2)} style={{ flex:1, padding:10, borderRadius:8, border:'1.5px solid #e2e8f0', background:'#fff', fontWeight:600, cursor:'pointer', color:'#374151' }}>← Back</button>
                  <button onClick={launchCampaign} disabled={creating} style={{ flex:2, padding:10, borderRadius:8, border:'none', background: creating?'#fde68a':'linear-gradient(135deg,#f59e0b,#d97706)', color:'#fff', fontWeight:700, cursor: creating?'not-allowed':'pointer' }}>
                    {creating ? 'Launching…' : '🚀 Launch Campaign'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
